import React, { useState } from "react";
import { useUserPreferences } from "../../../../core/context/UserPreferencesContext";
import { FormField, Select } from "../../../../design-system/components/inputs";
import { ThemeToggle, PrimaryButton } from "../../../../design-system";
import { useTheme } from "../../../../core/context/ThemeContext";
import { typography, surfaces, borders, textColors, interactive } from "../../../../design-system/tokens";

const PreferencesSettings = ({ username }) => {
  const { preferences, loading, updatePreferences, timezoneOptions } = useUserPreferences();
  const { theme } = useTheme();
  const [saving, setSaving] = useState(false);
  const [localPreferences, setLocalPreferences] = useState(preferences);

  // Update local state when global preferences change
  React.useEffect(() => {
    setLocalPreferences(preferences);
  }, [preferences]);

  const handlePreferenceChange = (key, value) => {
    setLocalPreferences(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await updatePreferences(localPreferences);
      if (result.success) {
        alert("Preferences saved successfully!");
      } else {
        alert(result.error || "Failed to save preferences. Please try again.");
      }
    } catch (err) {
      console.error("Error saving user preferences:", err);
      alert("Network error. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className={textColors.secondary}>Loading user preferences...</div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Localization */}
      <div className={`rounded-2xl shadow-sm ${surfaces.glassCard} border ${borders.glass}`}>
        <div className={`px-6 py-4 border-b ${borders.glass}`}>
          <h2 className={`${typography.h5} ${textColors.primary}`}>Language and Timezone</h2>
          <p className={`${typography.bodySmall} ${textColors.secondary} mt-1`}>Set your language and timezone preferences.</p>
        </div>
        
        <div className="p-6 space-y-4">
          <FormField 
            label="Language" 
            htmlFor="language"
          >
            <Select
              id="language"
              value={localPreferences.language}
              onChange={(e) => handlePreferenceChange("language", e.target.value)}
              options={[{ value: "en", label: "English" }]}
            />
          </FormField>

          <FormField 
            label="Timezone" 
            htmlFor="timezone"
          >
            <Select
              id="timezone"
              value={localPreferences.timezone}
              onChange={(e) => handlePreferenceChange("timezone", e.target.value)}
              options={
                timezoneOptions && timezoneOptions.length > 0
                  ? timezoneOptions
                  : [
                      { value: "America/New_York", label: "Eastern Time (EST) (UTC-05:00)" },
                      { value: "America/Chicago", label: "Central Time (CST) (UTC-06:00)" },
                      { value: "America/Denver", label: "Mountain Time (MST) (UTC-07:00)" },
                      { value: "America/Los_Angeles", label: "Pacific Time (PST) (UTC-08:00)" },
                      { value: "Europe/London", label: "London (GMT) (UTC+00:00)" },
                      { value: "Europe/Paris", label: "Paris (CET) (UTC+01:00)" },
                      { value: "Asia/Tokyo", label: "Tokyo (JST) (UTC+09:00)" },
                    ]
              }
            />
          </FormField>
        </div>
      </div>

      {/* Theme Preference */}
      <div className={`rounded-2xl shadow-sm ${surfaces.glassCard} border ${borders.glass}`}>
        <div className={`px-6 py-4 border-b ${borders.glass}`}>
          <h2 className={`${typography.h5} ${textColors.primary}`}>Theme Preference</h2>
          <p className={`${typography.bodySmall} ${textColors.secondary} mt-1`}>Choose between light and dark mode for the editor interface.</p>
        </div>
        
        <div className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className={`${typography.body} ${textColors.primary}`}>
                Current theme: <strong className={`${textColors.primary} font-semibold`}>{theme === 'dark' ? 'Dark' : 'Light'} Mode</strong>
              </p>
              <p className={`${typography.bodySmall} ${textColors.secondary} mt-1`}>
                Toggle between light and dark theme for a comfortable editing experience.
              </p>
            </div>
            <ThemeToggle />
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <PrimaryButton
          onClick={handleSave}
          loading={saving}
          className={interactive.primaryGradientBtn}
        >
          Save Preferences
        </PrimaryButton>
      </div>
    </div>
  );
};

export default PreferencesSettings; 